import Badge from '@atlaskit/badge';
import ArrowLeftIcon from '@atlaskit/icon/glyph/arrow-left';
import ArrowRightIcon from '@atlaskit/icon/glyph/arrow-right';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel';
import FilterIcon from '@atlaskit/icon/glyph/filter';
import HipchatChevronDoubleDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-double-down';
import HipchatChevronDoubleUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-double-up';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import { TriggerProps } from '@atlaskit/popup';
import { Box, Flex, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import {
  getDatabaseContainerType,
  SendDatabaseSortEventSources,
  sendSortEvent,
  useAnalytics,
} from '@databases/analytics';
import ContextualMenu, {
  ButtonItem,
  MenuData,
  MenuItem,
  tooltip,
  useContextualMenu,
} from '@databases/contextual-menu';
import {
  addColumnAfter,
  addColumnBefore,
  backspace,
  deleteRow,
  Keymap,
  makeKeyMapWithCommon,
  moveColumnLeft,
  moveColumnRight,
} from '@databases/contextual-menu/keymaps';
import { useDialog } from '@databases/dialog';
import { useBooleanFeatureFlag } from '@databases/feature-flags/hooks';
import {
  ActiveHorizontalDragHandlerIcon,
  getSummationIcon,
  HideIcon,
  HorizontalDragHandlerIcon,
  InsertColumnAfterIcon,
  InsertColumnBeforeIcon,
} from '@databases/icons';
import { messages } from '@databases/intl/messages';
import {
  ADD_LEFT_SHORTCUT,
  ADD_RIGHT_SHORTCUT,
  CLEAR_SHORTCUT,
  DELETE_SHORTCUT,
  DUPLICATE_SHORTCUT,
  MOVE_LEFT_SHORTCUT,
  MOVE_RIGHT_SHORTCUT,
} from '@databases/keyboard-shortcuts/shortcuts';
import usePanel from '@databases/panel/usePanel';
import QuickCalculationValues from '@databases/quick-calc/QuickCalculationValues';
import { isQuickCalcDisabledFieldTypes } from '@databases/quick-calc/utils';
import {
  HoverProps,
  KeyboardShortcut,
  useComponent,
  useKeyboardShortcut,
} from '@databases/react';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { setHoverDeleteButton } from '@databases/redux/cellSelections/actions';
import {
  isFieldSelected,
  selectHasFieldAfterSelection,
  selectHasFieldBeforeSelection,
  selectIsDeleteColumnsDisabled,
  selectIsEntireFieldSelected,
  selectIsHoverDeleteFieldsButton,
  selectSelectedEntireFieldIds,
  selectSelectedEntireFieldsCount,
} from '@databases/redux/cellSelections/selectors';
import {
  doClearEntireFields,
  doDeleteFields,
  doDuplicateEntireFields,
  doInsertFields,
  doMoveFields,
  doSelectNewFields,
} from '@databases/redux/contextual-menu/actions';
import {
  DatabaseFilter,
  DatabaseFilterUsageType,
  DatabaseViewSettings,
  DEFAULT_DATABASE_VIEW_SETTINGS,
} from '@databases/types/database/view';
import {
  getDatabaseFieldRequired,
  getDatabaseFields,
} from '@databases/utils/database/utils';
import { ViewChangeType, ViewChangeTypes } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { messages as viewSettingsMessages } from '@databases/views/messages';
import { ViewPanelSettingType } from '@databases/views/types';
import { useFilterSettings } from '@databases/views/useFilterSettings';
import useView from '@databases/views/useView';
import { useViewUiState } from '@databases/views/useViewUiState';
import React, { FunctionComponent, useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { TABLE_CLASSNAMES } from '../../../../constants';
import { IGNORED_TABLE_ELEMENT_CLASS } from '../../TableResizer';

import { ColSelectButtonContainer, DragHandlerWrapper } from './styles';
import { Position } from './type';

const elementBeforeIconWrapper = xcss({
  marginRight: 'space.negative.075',
});

const elementAfterShortCutWrapper = xcss({
  backgroundColor: 'color.background.neutral',
  color: 'color.text.subtlest',
  borderRadius: 'border.radius.050',
  padding: 'space.050',
  lineHeight: 1,
  fontSize: 11.67,
  alignSelf: 'flex-end',
});

enum SELECT_FIELD_MENU_ACTIONS {
  ADD_FIELD_LEFT = 'add_field_left',
  ADD_FIELD_RIGHT = 'add_field_right',
  DUPLICATE_FIELDS = 'duplicate_fields',
  CLEAR_CELLS = 'clear_cells',
  DELETE_FIELDS = 'delete_fields',
  MOVE_FIELDS_LEFT = 'move_fields_left',
  MOVE_FIELDS_RIGHT = 'move_fields_right',
  FILTER_FIELD = 'filter_field',
  SORT_FIELD_INCREASING = 'sort_field_increasing',
  SORT_FIELD_DECREASING = 'sort_field_decreasing',
  HIDE_FIELDS = 'hide_fields',
}

const SELECT_FIELD_SHORTCUTS: Record<
  SELECT_FIELD_MENU_ACTIONS,
  { keyboardShortCut: KeyboardShortcut[]; keyMap: Keymap } | undefined
> = {
  [SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_LEFT]: {
    keyMap: addColumnBefore,
    keyboardShortCut: ADD_LEFT_SHORTCUT,
  },
  [SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_RIGHT]: {
    keyMap: addColumnAfter,
    keyboardShortCut: ADD_RIGHT_SHORTCUT,
  },
  [SELECT_FIELD_MENU_ACTIONS.DUPLICATE_FIELDS]: {
    keyMap: makeKeyMapWithCommon('Copy', 'Mod-d'),
    keyboardShortCut: DUPLICATE_SHORTCUT,
  },
  [SELECT_FIELD_MENU_ACTIONS.CLEAR_CELLS]: {
    keyMap: backspace,
    keyboardShortCut: CLEAR_SHORTCUT,
  },
  [SELECT_FIELD_MENU_ACTIONS.DELETE_FIELDS]: {
    keyMap: deleteRow,
    keyboardShortCut: DELETE_SHORTCUT,
  },
  [SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_LEFT]: {
    keyMap: moveColumnLeft,
    keyboardShortCut: MOVE_LEFT_SHORTCUT,
  },
  [SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_RIGHT]: {
    keyMap: moveColumnRight,
    keyboardShortCut: MOVE_RIGHT_SHORTCUT,
  },
  [SELECT_FIELD_MENU_ACTIONS.FILTER_FIELD]: undefined,
  [SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_INCREASING]: undefined,
  [SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_DECREASING]: undefined,
  [SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS]: undefined,
};

type SelectFieldMenuProps = {
  contentId: string;
  scope: string;
  fieldId: string;
  currentViewId: string;
  viewSettings?: DatabaseViewSettings;
  hoverProps?: HoverProps;
  canEditFields: boolean;
  handleSelectColumn: (e: React.MouseEvent | React.KeyboardEvent) => void;
};

const SelectFieldMenu: FunctionComponent<SelectFieldMenuProps> = ({
  contentId,
  scope,
  fieldId,
  currentViewId,
  viewSettings,
  hoverProps,
  handleSelectColumn,
  canEditFields,
}) => {
  const isQuickCalcEnabled = useBooleanFeatureFlag('databases-frontend-quick-calc');

  const [
    canDeleteFields,
    canDuplicateFields,
    canInsertFields,
    canClearCells,
    canMoveRight,
    canMoveLeft,
    isSelected,
    selectedFieldsCount,
    isHoverDeleteFieldsButton,
  ] = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
    const isSelected = selectIsEntireFieldSelected(state, {
      fieldId,
    });
    const selectedFieldsCount = isSelected
      ? selectSelectedEntireFieldsCount(state, {
          contentId,
        })
      : 0;
    const isDeletionDisabled =
      isSelected &&
      selectIsDeleteColumnsDisabled(state, {
        contentId,
      });
    const hasFieldBeforeSelection =
      isSelected &&
      selectHasFieldBeforeSelection(state, {
        contentId,
      });
    const hasFieldAfterSelection =
      isSelected &&
      selectHasFieldAfterSelection(state, {
        contentId,
      });
    const canInsertFields = canEditFields && selectedFieldsCount > 0;
    const canDuplicateFields = canEditFields && selectedFieldsCount > 0;
    const canDeleteFields =
      canEditFields && selectedFieldsCount > 0 && !isDeletionDisabled;
    const canClearCells = canEditFields && selectedFieldsCount > 0;
    const canMoveRight =
      canEditFields && selectedFieldsCount > 0 && hasFieldAfterSelection;
    const canMoveLeft =
      canEditFields && selectedFieldsCount > 0 && hasFieldBeforeSelection;
    return [
      canDeleteFields,
      canDuplicateFields,
      canInsertFields,
      canClearCells,
      canMoveRight,
      canMoveLeft,
      isSelected,
      selectedFieldsCount,
      isSelected && selectIsHoverDeleteFieldsButton(state),
    ] as const;
  });

  const { formatMessage } = useIntl();
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();
  const { getData, hidePanel, showPanel } = usePanel();
  const { fieldId: panelFieldId } = getData<'FieldSettingsPanel'>() || {};

  const menuData: MenuData = {
    type: 'field',
    fieldId,
  };
  const { isMenuOpen, toggleOpenMenu } = useContextualMenu({
    type: 'field',
    fieldId,
  });

  const prevIsSelected = useRef(isSelected);
  const handleMouseDown = useCallback(
    async (e: React.MouseEvent) => {
      if (!isMenuOpen) {
        await client.sendFieldContextualMenuOpened({
          contentId: contentId,
          databaseContainer: getDatabaseContainerType(),
        });
      }
      toggleOpenMenu();
      if (!isSelected) {
        handleSelectColumn(e);
        prevIsSelected.current = true;
      } else {
        prevIsSelected.current = false;
      }
    },
    [client, contentId, handleSelectColumn, isMenuOpen, isSelected, toggleOpenMenu]
  );

  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (isSelected && !prevIsSelected.current && !isMenuOpen) {
        // remove selection if the user clicks on the select button again
        handleSelectColumn(e);
      }
    },
    [handleSelectColumn, isMenuOpen, isSelected]
  );

  const handleRightClick = (e: React.MouseEvent) => e.preventDefault();

  const Trigger = useComponent(
    ({ ref, ...triggerProps }: TriggerProps) => (
      <ColSelectButtonContainer
        className={IGNORED_TABLE_ELEMENT_CLASS}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onContextMenu={handleRightClick}
        {...triggerProps}
        {...hoverProps}
        ref={ref}
      >
        <DragHandlerWrapper
          className={TABLE_CLASSNAMES.SELECT_BUTTON}
          isActive={isMenuOpen && isSelected}
          isDeletionActive={isHoverDeleteFieldsButton}
        >
          <div className={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}>
            <ActiveHorizontalDragHandlerIcon
              label={TABLE_CLASSNAMES.ACTIVE_DRAG_HANDLER_ICON}
            />
          </div>
          <div className={TABLE_CLASSNAMES.DRAG_HANDLER_ICON}>
            <HorizontalDragHandlerIcon label={TABLE_CLASSNAMES.DRAG_HANDLER_ICON} />
          </div>
        </DragHandlerWrapper>
      </ColSelectButtonContainer>
    ),
    [
      handleMouseDown,
      handleMouseUp,
      hoverProps,
      isMenuOpen,
      isSelected,
      isHoverDeleteFieldsButton,
    ]
  );

  const handleInsertFields = useCallback(
    async (position: Position) => {
      await dispatch(
        doSelectNewFields(contentId, () =>
          dispatch(doInsertFields(contentId, currentViewId, position === Position.BEFORE))
        )
      );
      await client.sendInsertRowEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
      });
    },
    [client, contentId, currentViewId, dispatch]
  );

  const handleDuplicateFields = useCallback(() => {
    dispatch(
      doSelectNewFields(contentId, () =>
        dispatch(doDuplicateEntireFields(contentId, scope))
      )
    );
  }, [dispatch, contentId, scope]);

  const handleClearFields = useCallback(() => {
    dispatch(doClearEntireFields(contentId));
  }, [contentId, dispatch]);

  const { showDialog } = useDialog();

  const onLeaveDeleteFieldButton = useCallback(() => {
    dispatch(setHoverDeleteButton({ value: null }));
  }, [dispatch]);

  const handleDeleteFields = useCallback(() => {
    const fieldNames: string[] = [];
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId,
    });
    const selectedEntireFieldIds = selectSelectedEntireFieldIds(store.getState(), {
      contentId,
    });
    selectedEntireFieldIds.forEach((fieldId) => {
      if (database.fieldsById[fieldId]) {
        fieldNames.push(database.fieldsById[fieldId].name);
      }
    });
    showDialog('ConfirmDialog', {
      heading: formatMessage(messages['field-settings.delete-field']),
      body: (
        <Stack space="space.150">
          <Box>
            <FormattedMessage
              {...messages[
                'field-settings.delete-multiple-fields-confirmation-message.fields'
              ]}
              values={{
                fieldsNumber: fieldNames.length,
                fields: <Text weight="bold">{fieldNames.join(', ')}</Text>,
              }}
            />
          </Box>
          <Box>
            {formatMessage(
              messages[
                'field-settings.delete-multiple-fields-confirmation-message.additional'
              ]
            )}
          </Box>
        </Stack>
      ),
      yesLabel: formatMessage(messages['delete-buttons.delete-field-button']),
      noLabel: formatMessage(messages['delete-buttons.delete-field-cancel-button']),
      appearance: 'danger',
      onClose: (result: boolean) => {
        onLeaveDeleteFieldButton();
        if (result) {
          if (isFieldSelected(store.getState(), contentId, panelFieldId)) {
            hidePanel();
          }
          dispatch(doDeleteFields(contentId));
          void client.sendDeleteFieldEvent({
            contentId,
            databaseContainer: getDatabaseContainerType(),
          });

          client.sendRemoveCalculationEvent({
            databaseContainer: getDatabaseContainerType(),
            fieldId,
            source: 'selectFieldMenu',
            contentId: database.contentId,
          });
        }
      },
    });
  }, [
    store,
    contentId,
    showDialog,
    formatMessage,
    client,
    fieldId,
    onLeaveDeleteFieldButton,
    panelFieldId,
    dispatch,
    hidePanel,
  ]);

  const handleMoveFields = useCallback(
    (position: Position) => {
      dispatch(doMoveFields(contentId, viewSettings, position === Position.BEFORE));
    },
    [contentId, dispatch, viewSettings]
  );

  const { setViewChange, view } = useView();
  const handleFilterChange = useCallback(
    (filterChange: ViewChangeType) => setViewChange(filterChange),
    [setViewChange]
  );
  const handleSortChange = useCallback(
    (sort: DatabaseViewSettings['sort']) => {
      const database = selectDatabaseByContentIdRequired(store.getState(), {
        contentId,
      });
      const field = getDatabaseFieldRequired(database, fieldId);
      setViewChange({
        type: ViewChangeTypes.VIEW_SORT,
        sort,
      });
      void sendSortEvent(
        client,
        sort,
        SendDatabaseSortEventSources.SORT_MENU,
        field ? field.type : undefined,
        contentId
      );
    },
    [client, contentId, fieldId, setViewChange, store]
  );

  const filters = view.settings.filter as
    | {
        type: DatabaseFilterUsageType;
        value: DatabaseFilter[];
      }
    | undefined;

  const fieldFilters = useMemo(
    () =>
      filters?.value.filter((filter) => 'fieldId' in filter && filter.fieldId === fieldId)
        .length || 0,
    [filters, fieldId]
  );

  const { handleAddFilter } = useFilterSettings({
    onChange: handleFilterChange,
    filterObject: DEFAULT_DATABASE_VIEW_SETTINGS.filter,
    contentId,
    store,
  });

  const { setActivePanelSetting } = useViewUiState();
  const handleFilterField = useCallback(() => {
    handleAddFilter(fieldId);
    setActivePanelSetting(ViewPanelSettingType.FILTER);
    showPanel('ViewSettingsPanel', {
      autoFocusLastFilterValue: true,
      canEditViews: canEditFields,
      unsavedChangesMessage: formatMessage(viewSettingsMessages['view.exit-dialog-body']),
    });
  }, [
    handleAddFilter,
    fieldId,
    setActivePanelSetting,
    showPanel,
    canEditFields,
    formatMessage,
  ]);

  const handleHideFields = useCallback(() => {
    const database = selectDatabaseByContentIdRequired(store.getState(), {
      contentId,
    });
    const fields = getDatabaseFields(database);
    const field = getDatabaseFieldRequired(database, fieldId);
    const viewChange: ViewChangeType = {
      type: ViewChangeTypes.VIEW_FIELDS,
      fieldKey: fieldId,
      fieldValue: false,
      fields,
    };
    setViewChange(viewChange);
    void client.sendHideColumnsEvent({
      source: 'databaseView',
      contentId: contentId,
      databaseContainer: getDatabaseContainerType(),
      hidden: true,
      fieldType: field.type,
    });
  }, [client, contentId, fieldId, setViewChange, store]);

  const addFieldLeftMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_LEFT,
      content: formatMessage(messages['field-actions.add-left']),
      closeOnClick: true,
      onClick: () => handleInsertFields(Position.BEFORE),
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_LEFT]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <InsertColumnBeforeIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleInsertFields]
  );

  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const closeSubmenu = useCallback(() => {
    setIsSubmenuOpen(false);
  }, []);
  const toggleSubmenu = useCallback(() => {
    setIsSubmenuOpen((isOpen) => !isOpen);
  }, []);

  const addFieldRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_RIGHT,
      content: formatMessage(messages['field-actions.add-right']),
      closeOnClick: true,
      onClick: () => handleInsertFields(Position.AFTER),
      onMouseEnter: closeSubmenu,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_RIGHT]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <InsertColumnAfterIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleInsertFields]
  );

  const duplicateFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.DUPLICATE_FIELDS,
      content: formatMessage(messages['field-actions.duplicate'], {
        fieldCount: selectedFieldsCount,
      }),
      closeOnClick: true,
      onClick: handleDuplicateFields,
      onMouseEnter: closeSubmenu,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.DUPLICATE_FIELDS]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CopyIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleDuplicateFields, selectedFieldsCount]
  );

  const clearCellsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.CLEAR_CELLS,
      content: formatMessage(messages['value-actions.clear-cells'], { valuesCount: 2 }),
      closeOnClick: true,
      onClick: handleClearFields,
      onMouseEnter: closeSubmenu,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.CLEAR_CELLS]?.keyMap)}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <CrossCircleIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleClearFields]
  );

  const onHoverDeleteFieldButton = useCallback(() => {
    dispatch(setHoverDeleteButton({ value: 'fields' }));
  }, [dispatch]);

  const deleteFieldsMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.DELETE_FIELDS,
      content: formatMessage(messages['field-actions.delete'], {
        fieldCount: selectedFieldsCount,
      }),
      closeOnClick: true,
      onClick: handleDeleteFields,
      onMouseEnter: onHoverDeleteFieldButton,
      onMouseLeave: onLeaveDeleteFieldButton,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.DELETE_FIELDS]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <TrashIcon
            label=""
            size="medium"
            primaryColor={
              isHoverDeleteFieldsButton
                ? token('color.icon.danger', colors.R500)
                : undefined
            }
          />
        </Flex>
      ),
    }),
    [
      formatMessage,
      handleDeleteFields,
      isHoverDeleteFieldsButton,
      onHoverDeleteFieldButton,
      onLeaveDeleteFieldButton,
      selectedFieldsCount,
    ]
  );

  const moveFieldsLeftMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_LEFT,
      content: formatMessage(messages['field-actions.move-left'], {
        fieldCount: selectedFieldsCount,
      }),
      closeOnClick: true,
      onClick: () => handleMoveFields(Position.BEFORE),
      onMouseEnter: closeSubmenu,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_LEFT]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <ArrowLeftIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleMoveFields, selectedFieldsCount]
  );

  const moveFieldsRightMenuItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_RIGHT,
      content: formatMessage(messages['field-actions.move-right'], {
        fieldCount: selectedFieldsCount,
      }),
      closeOnClick: true,
      onClick: () => handleMoveFields(Position.AFTER),
      onMouseEnter: closeSubmenu,
      elemAfter: (
        <Inline xcss={elementAfterShortCutWrapper}>
          {tooltip(
            SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_RIGHT]?.keyMap
          )}
        </Inline>
      ),
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <ArrowRightIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleMoveFields, selectedFieldsCount]
  );

  const filterFieldItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.FILTER_FIELD,
      content: formatMessage(messages['field-actions.filter']),
      closeOnClick: true,
      onClick: handleFilterField,
      onMouseEnter: closeSubmenu,
      elemAfter:
        fieldFilters > 0 ? (
          <Badge
            style={{
              backgroundColor: token(
                'color.background.accent.blue.subtlest.hovered',
                '#CCE0FF'
              ),
            }}
            testId="filters-badge"
          >
            {fieldFilters}
          </Badge>
        ) : undefined,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <FilterIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [formatMessage, handleFilterField, closeSubmenu, fieldFilters]
  );

  const sortFieldIncreasingItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_INCREASING,
      content: formatMessage(messages['field-actions.sort-increasing']),
      closeOnClick: true,
      onClick: () =>
        handleSortChange({
          fieldId,
          ascending: true,
        }),
      onMouseEnter: closeSubmenu,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <HipchatChevronDoubleUpIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, fieldId, formatMessage, handleSortChange]
  );

  const sortFieldDecreasingItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.SORT_FIELD_DECREASING,
      content: formatMessage(messages['field-actions.sort-decreasing']),
      closeOnClick: true,
      onClick: () =>
        handleSortChange({
          fieldId,
          ascending: false,
        }),
      onMouseEnter: closeSubmenu,
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <HipchatChevronDoubleDownIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, fieldId, formatMessage, handleSortChange]
  );

  const hideFieldsItem: MenuItem = useMemo(
    () => ({
      key: SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS,
      content: formatMessage(messages['field-actions.hide-fields'], {
        fieldCount: selectedFieldsCount,
      }),
      closeOnClick: true,
      onClick: handleHideFields,
      onMouseEnter: closeSubmenu,
      value: { name: SELECT_FIELD_MENU_ACTIONS.HIDE_FIELDS },
      elemBefore: (
        <Flex xcss={elementBeforeIconWrapper}>
          <HideIcon label="" size="medium" />
        </Flex>
      ),
    }),
    [closeSubmenu, formatMessage, handleHideFields, selectedFieldsCount]
  );

  const calculationMenuItemTrigger = useComponent(
    ({ ref, ...triggerProps }) => {
      const database = selectDatabaseByContentIdRequired(store.getState(), {
        contentId,
      });
      const field = getDatabaseFieldRequired(database, fieldId);

      const isDisabled = isQuickCalcDisabledFieldTypes(field.type);

      const SummationIcon = getSummationIcon(
        token(isDisabled ? 'color.text.disabled' : 'color.text')
      );

      return (
        <ToolTip
          content={
            isDisabled
              ? formatMessage(messages['quick.calculations.field-disabled-text'])
              : ''
          }
          position="auto"
        >
          {(tooltipProps) => (
            <Box {...tooltipProps}>
              <ButtonItem
                isDisabled={isDisabled}
                iconAfter={
                  isDisabled ? (
                    <EditorPanelIcon label={''} />
                  ) : (
                    <ChevronRightIcon label="" size="medium" />
                  )
                }
                iconBefore={<SummationIcon label="" />}
                onClick={toggleSubmenu}
                onMouseEnter={() => setIsSubmenuOpen(true)}
                ref={ref}
                {...triggerProps}
              >
                {formatMessage(messages['quick.calculations.placeholder-text'])}
              </ButtonItem>
            </Box>
          )}
        </ToolTip>
      );
    },
    [contentId, fieldId, formatMessage, store, toggleSubmenu]
  );

  const quickCalculationItem: MenuItem = useMemo(
    () => ({
      key: 'SELECT_VALUE_MENU_ACTIONS.QUICK_CALCULATION',
      name: 'quick-calc',
      closeOnClick: true,
      content:
        isQuickCalcEnabled && canEditFields ? (
          <QuickCalculationValues
            scope={scope}
            contentId={contentId}
            removeSelectedContent={() => {}}
            Trigger={calculationMenuItemTrigger}
            isOpen={isSubmenuOpen}
            onSelect={() => {
              toggleOpenMenu();
              setIsSubmenuOpen(false);
            }}
            placement="right"
            offset={[0, 2]}
          />
        ) : null,
      value: { name: 'SELECT_VALUE_MENU_ACTIONS.QUICK_CALCULATION' },
    }),
    [
      isQuickCalcEnabled,
      canEditFields,
      scope,
      contentId,
      calculationMenuItemTrigger,
      isSubmenuOpen,
      toggleOpenMenu,
    ]
  );

  const menuItems = useMemo(() => {
    const firstGroup: MenuItem[] = [];
    const secondGroup: MenuItem[] = [];
    const thirdGroup: MenuItem[] = [];
    if (canInsertFields) {
      firstGroup.push(addFieldRightMenuItem);
      firstGroup.push(addFieldLeftMenuItem);
    }
    if (canDuplicateFields) {
      firstGroup.push(duplicateFieldsMenuItem);
    }
    if (canClearCells) {
      firstGroup.push(clearCellsMenuItem);
    }
    if (canDeleteFields) {
      firstGroup.push(deleteFieldsMenuItem);
    }
    if (canMoveLeft) {
      secondGroup.push(moveFieldsLeftMenuItem);
    }
    if (canMoveRight) {
      secondGroup.push(moveFieldsRightMenuItem);
    }
    if (selectedFieldsCount === 1) {
      thirdGroup.push(quickCalculationItem);
      thirdGroup.push(filterFieldItem);
      thirdGroup.push(sortFieldIncreasingItem);
      thirdGroup.push(sortFieldDecreasingItem);
      thirdGroup.push(hideFieldsItem);
    }
    return [{ items: firstGroup }, { items: secondGroup }, { items: thirdGroup }];
  }, [
    canInsertFields,
    canDuplicateFields,
    canClearCells,
    canDeleteFields,
    canMoveLeft,
    canMoveRight,
    selectedFieldsCount,
    addFieldRightMenuItem,
    addFieldLeftMenuItem,
    duplicateFieldsMenuItem,
    clearCellsMenuItem,
    deleteFieldsMenuItem,
    moveFieldsLeftMenuItem,
    moveFieldsRightMenuItem,
    quickCalculationItem,
    filterFieldItem,
    sortFieldIncreasingItem,
    sortFieldDecreasingItem,
    hideFieldsItem,
  ]);

  useKeyboardShortcut(
    isMenuOpen && canInsertFields
      ? SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_LEFT]?.keyboardShortCut
      : undefined,
    () => handleInsertFields(Position.BEFORE)
  );
  useKeyboardShortcut(
    isMenuOpen && canInsertFields
      ? SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.ADD_FIELD_RIGHT]
          ?.keyboardShortCut
      : undefined,
    () => handleInsertFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canDuplicateFields
      ? SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.DUPLICATE_FIELDS]
          ?.keyboardShortCut
      : undefined,
    handleDuplicateFields
  );
  useKeyboardShortcut(
    isMenuOpen && canClearCells
      ? SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.CLEAR_CELLS]?.keyboardShortCut
      : undefined,
    handleClearFields
  );
  useKeyboardShortcut(
    isMenuOpen && canDeleteFields
      ? SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.DELETE_FIELDS]?.keyboardShortCut
      : undefined,
    handleDeleteFields
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveRight
      ? SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_RIGHT]
          ?.keyboardShortCut
      : undefined,
    () => handleMoveFields(Position.AFTER)
  );
  useKeyboardShortcut(
    isMenuOpen && canMoveLeft
      ? SELECT_FIELD_SHORTCUTS[SELECT_FIELD_MENU_ACTIONS.MOVE_FIELDS_LEFT]
          ?.keyboardShortCut
      : undefined,
    () => () => handleMoveFields(Position.BEFORE)
  );

  return (
    <ContextualMenu
      menuData={menuData}
      items={menuItems}
      trigger={Trigger}
      placement="bottom-start"
    />
  );
};

export default React.memo(SelectFieldMenu);
